import type { AthenaAdjunto, AthenaHilo, AthenaHiloEnLista, AthenaMensaje, DestinoDeAdjunto } from '@conversia/shared-types';
import { api } from './client';

/**
 * Athena con memoria (tanda 6): sus hilos, sus mensajes y los archivos que se le suben.
 * Prefijo /api/athena, del módulo `inspector`. Cada quien ve solo lo suyo: el hilo o el
 * adjunto de otra persona responde 404.
 */
export interface HiloConMensajes {
  hilo: AthenaHilo;
  mensajes: AthenaMensaje[];
}

const hilo = (id: string) => `/api/athena/hilos/${encodeURIComponent(id)}`;
const adjunto = (id: string) => `/api/athena/adjuntos/${encodeURIComponent(id)}`;

export const listarHilos = () => api.get<{ hilos: AthenaHiloEnLista[] }>('/api/athena/hilos');
export const crearHilo = () => api.post<{ hilo: AthenaHilo }>('/api/athena/hilos');
export const leerHilo = (id: string) => api.get<HiloConMensajes>(hilo(id));
export const renombrarHilo = (id: string, titulo: string) => api.patch<{ hilo: AthenaHilo }>(hilo(id), { titulo });
export const borrarHilo = (id: string) => api.del<{ ok: true }>(hilo(id));

/**
 * Manda un mensaje (texto, archivos ya subidos o las dos cosas) y devuelve la pregunta guardada
 * y lo que contestó Athena. Si algo falla al contestar, el servidor igual responde 200 con una
 * disculpa: la pregunta no se pierde.
 */
export const mandarMensaje = (id: string, mensaje: { texto?: string; adjuntos?: string[] }) =>
  api.post<{ pregunta: AthenaMensaje; respuesta: AthenaMensaje }>(`${hilo(id)}/mensajes`, mensaje);

/** Sube un archivo. Qué es lo decide el servidor por su firma; uno que no recibe responde 422 con el porqué. */
export const subirAdjunto = (archivo: File) => {
  const form = new FormData();
  form.append('file', archivo);
  return api.post<{ adjunto: AthenaAdjunto }>('/api/athena/adjuntos', form);
};

/** Lleva un adjunto a un proyecto. Con `hiloId`, Athena deja dicho en ese hilo lo que hizo. */
export const llevarAdjunto = (id: string, a: { destino: DestinoDeAdjunto; proyectoId: string; hiloId?: string }) =>
  api.post<{ ok: true; mensaje?: AthenaMensaje }>(`${adjunto(id)}/llevar`, a);

/** Importa un chat exportado de WhatsApp. `yoSoy` va tal cual lo trae el botón de Athena. */
export const importarChat = (id: string, a: { hiloId?: string; yoSoy?: string }) =>
  api.post<{ ok: true; conversationId: string; nombre: string; importados: number; repetidos: number; mensaje?: AthenaMensaje }>(
    `${adjunto(id)}/importar-chat`,
    a,
  );
